import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  getCookieConsent,
  type StoredCookieConsent,
} from "../../constants/cookies";

const measurementId =
  import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();

const GOOGLE_ANALYTICS_SCRIPT_ID =
  "gluviacare-google-analytics";

type GtagCommand = [
  command: string,
  targetOrEvent: string | Date,
  parameters?: Record<string, unknown>,
];

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: GtagCommand) => void;
  }
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];

  window.gtag =
    window.gtag ||
    function gtag(...args: GtagCommand) {
      window.dataLayer.push(args);
    };
}

function updateAnalyticsConsent(
  granted: boolean,
) {
  ensureGtag();

  window.gtag("consent", "update", {
    analytics_storage: granted
      ? "granted"
      : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

function removeGoogleAnalyticsCookies() {
  document.cookie.split(";").forEach((cookie) => {
    const cookieName = cookie
      .split("=")[0]
      ?.trim();

    if (
      cookieName === "_ga" ||
      cookieName?.startsWith("_ga_")
    ) {
      document.cookie = [
        `${cookieName}=`,
        "Max-Age=0",
        "Path=/",
        `Domain=.${window.location.hostname}`,
        "SameSite=Lax",
      ].join("; ");

      document.cookie = [
        `${cookieName}=`,
        "Max-Age=0",
        "Path=/",
        "SameSite=Lax",
      ].join("; ");
    }
  });
}

function hasAnalyticsConsent() {
  return (
    getCookieConsent()?.preferences.analytics ===
    true
  );
}

export function GoogleAnalytics() {
  const location = useLocation();

  const [analyticsAllowed, setAnalyticsAllowed] =
    useState(hasAnalyticsConsent);

  const [analyticsReady, setAnalyticsReady] =
    useState(false);

  /*
   * Keep Analytics synchronized with the
   * GluviaCare cookie-preference controls.
   */
  useEffect(() => {
    function handleConsentChange(
      event: Event,
    ) {
      const consentEvent =
        event as CustomEvent<StoredCookieConsent>;

      const allowed =
        consentEvent.detail?.preferences
          ?.analytics === true;

      setAnalyticsAllowed(allowed);

      if (!allowed) {
        updateAnalyticsConsent(false);
        removeGoogleAnalyticsCookies();
        setAnalyticsReady(false);
      }
    }

    function handleStorageChange(
      event: StorageEvent,
    ) {
      if (
        event.key ===
        COOKIE_CONSENT_STORAGE_KEY
      ) {
        setAnalyticsAllowed(
          hasAnalyticsConsent(),
        );
      }
    }

    window.addEventListener(
      "gluviacare:cookie-consent",
      handleConsentChange,
    );

    window.addEventListener(
      "storage",
      handleStorageChange,
    );

    return () => {
      window.removeEventListener(
        "gluviacare:cookie-consent",
        handleConsentChange,
      );

      window.removeEventListener(
        "storage",
        handleStorageChange,
      );
    };
  }, []);

  /*
   * Load Google Analytics only after the
   * visitor enables analytics cookies.
   */
  useEffect(() => {
    if (!measurementId || !analyticsAllowed) {
      return;
    }

    ensureGtag();

    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });

    updateAnalyticsConsent(true);

    window.gtag("js", new Date());

    window.gtag("config", measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });

    const existingScript =
      document.getElementById(
        GOOGLE_ANALYTICS_SCRIPT_ID,
      ) as HTMLScriptElement | null;

    if (existingScript) {
      setAnalyticsReady(true);
      return;
    }

    const script =
      document.createElement("script");

    script.id =
      GOOGLE_ANALYTICS_SCRIPT_ID;

    script.async = true;

    script.src =
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        measurementId,
      )}`;

    script.addEventListener("load", () => {
      setAnalyticsReady(true);
    });

    document.head.appendChild(script);
  }, [analyticsAllowed]);

  /*
   * Record page views whenever React Router
   * changes the visible page.
   */
  useEffect(() => {
    if (
      !measurementId ||
      !analyticsAllowed ||
      !analyticsReady
    ) {
      return;
    }

    const pagePath = [
      location.pathname,
      location.search,
      location.hash,
    ].join("");

    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: pagePath,
      send_to: measurementId,
    });
  }, [
    analyticsAllowed,
    analyticsReady,
    location.hash,
    location.pathname,
    location.search,
  ]);

  return null;
}